import {ref, computed} from 'vue'
import {usePosDatabase} from './usePosDatabase.js'
import {useRuntimeConfig} from '#app'
import {redactSensitive} from '~/utils/sanitize.js'

const TYRO_URLS = {
  simulator: 'https://iclientsimulator.test.tyro.com',
  test: 'https://iclient.test.tyro.com',
  production: 'https://iclient.tyro.com',
}

const getPosProductInfo = () => {
  const config = useRuntimeConfig()
  return {
    posProductVendor: 'Starvly',
    posProductName: 'Starvly POS',
    posProductVersion: config.public.appVersion || '1.0.0',
  }
}

const RETRY_CONFIG = {
  maxRetries: 3,
  initialDelay: 1000,
  maxDelay: 10000,
  backoffMultiplier: 2,
}

const ERROR_CODES = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  TERMINAL_DISCONNECTED: 'TERMINAL_DISCONNECTED',
  TIMEOUT: 'TIMEOUT',
  CANCELLED: 'CANCELLED',
  DECLINED: 'DECLINED',
  UNKNOWN: 'UNKNOWN',
}

const isScriptLoaded = ref(false)
const isInitialized = ref(false)
const isPaired = ref(false)
const isProcessing = ref(false)
const currentStatus = ref('')
const lastError = ref(null)
const terminalInfo = ref(null)
const connectionState = ref('disconnected')
const retryAttempt = ref(0)
const hasPendingTransaction = ref(false)
const integrationKey = ref(null)

let iclientInstance = null
let reconnectTimer = null
let currentTerminalId = null

const PENDING_TXN_KEY = 'tyro_pending_transaction'

export const useTyro = () => {
  const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms))

  const calculateBackoff = (attempt) => {
    const delay = RETRY_CONFIG.initialDelay * Math.pow(RETRY_CONFIG.backoffMultiplier, attempt)
    return Math.min(delay, RETRY_CONFIG.maxDelay)
  }

  const classifyError = (error) => {
    const message = (error?.message || error?.result || '').toLowerCase()

    if (message.includes('network') || message.includes('connection') || message.includes('offline')) {
      return ERROR_CODES.NETWORK_ERROR
    }
    if (message.includes('terminal') && (message.includes('disconnect') || message.includes('unavailable'))) {
      return ERROR_CODES.TERMINAL_DISCONNECTED
    }
    if (message.includes('timeout') || message.includes('timed out')) {
      return ERROR_CODES.TIMEOUT
    }
    if (message.includes('cancel') || error?.result === 'CANCELLED') {
      return ERROR_CODES.CANCELLED
    }
    if (message.includes('decline') || error?.result === 'DECLINED') {
      return ERROR_CODES.DECLINED
    }
    return ERROR_CODES.UNKNOWN
  }

  const isRetryableError = (errorCode) => {
    return [ERROR_CODES.NETWORK_ERROR, ERROR_CODES.TERMINAL_DISCONNECTED, ERROR_CODES.TIMEOUT].includes(errorCode)
  }

  const loadScript = async (environment = 'simulator') => {
    if (isScriptLoaded.value) {
      console.log('[Tyro] Script already loaded')
      return true
    }

    const baseUrl = TYRO_URLS[environment] || TYRO_URLS.simulator
    const scriptUrl = `${baseUrl}/iclient-with-ui-v1.js`

    return new Promise((resolve, reject) => {
      const existingScript = document.querySelector(`script[src="${scriptUrl}"]`)
      if (existingScript) {
        isScriptLoaded.value = true
        resolve(true)
        return
      }

      const script = document.createElement('script')
      script.src = scriptUrl
      script.async = true

      const timeout = setTimeout(() => {
        lastError.value = 'Script load timeout'
        reject(new Error('Tyro iClient script load timeout'))
      }, 30000)

      script.onload = () => {
        clearTimeout(timeout)
        console.log('[Tyro] iClient script loaded successfully')
        isScriptLoaded.value = true
        resolve(true)
      }

      script.onerror = (error) => {
        clearTimeout(timeout)
        console.error('[Tyro] Failed to load iClient script:', error)
        lastError.value = 'Failed to load Tyro iClient script'
        reject(new Error('Failed to load Tyro iClient script'))
      }

      document.head.appendChild(script)
    })
  }

  const clearCachedCredentials = async () => {
    try {
      const keysToRemove = []
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && (key.includes('tyro') || key.includes('iclient') || key.includes('TYRO'))) {
          keysToRemove.push(key)
        }
      }
      keysToRemove.forEach(key => {
        console.log('[Tyro] Removing cached key:', key)
        localStorage.removeItem(key)
      })

      try {
        const { deletePosSetting, isDbReady } = usePosDatabase()
        if (isDbReady.value) {
          const terminalId = currentTerminalId || 'default'
          const pairingKey = `tyro_paired_${terminalId}`
          await deletePosSetting(pairingKey)
          console.log('[Tyro] Cleared SQLite pairing cache for:', pairingKey)
        }
      } catch (dbErr) {
        console.warn('[Tyro] Failed to clear SQLite cache:', dbErr)
      }

      isPaired.value = false
      integrationKey.value = null
      console.log('[Tyro] Cleared cached credentials')
    } catch (e) {
      console.warn('[Tyro] Failed to clear cache:', e)
    }
  }

  const checkCachedPairing = async (terminalId) => {
    try {
      const { getPosSetting, isDbReady } = usePosDatabase()
      if (!isDbReady.value) {
        console.warn('[Tyro] Database not ready, cannot check cached pairing')
        return false
      }
      const pairingKey = `tyro_paired_${terminalId || 'default'}`
      console.log('[Tyro] Checking cached pairing for key:', pairingKey)
      const pairedValue = await getPosSetting(pairingKey)
      console.log('[Tyro] Cached pairing value:', pairedValue)
      if (pairedValue === '1' || pairedValue === 'true') {
        console.log('[Tyro] Found cached pairing in SQLite for:', pairingKey)
        return true
      }
      return false
    } catch (e) {
      console.warn('[Tyro] Failed to check cached pairing:', e)
      return false
    }
  }

  const savePairingState = async (terminalId, paired) => {
    try {
      const { savePosSetting, isDbReady } = usePosDatabase()
      if (!isDbReady.value) {
        console.warn('[Tyro] Database not ready, cannot save pairing state')
        return
      }
      const pairingKey = `tyro_paired_${terminalId || 'default'}`
      await savePosSetting(pairingKey, paired ? '1' : '0')
      console.log('[Tyro] Saved pairing state to SQLite:', pairingKey, paired)
      const verify = await usePosDatabase().getPosSetting(pairingKey)
      console.log('[Tyro] Verified saved pairing state:', verify)
    } catch (e) {
      console.warn('[Tyro] Failed to save pairing state:', e)
    }
  }

  const bringTyroToFront = () => {
    const iframes = document.querySelectorAll('iframe[src*="tyro"], iframe[src*="iclient"]')
    iframes.forEach(iframe => {
      iframe.style.zIndex = '2147483647'
      iframe.style.position = 'fixed'
    })
    const tyroElements = document.querySelectorAll('[class*="tyro"], [class*="iclient"], [id*="tyro"], [id*="iclient"]')
    tyroElements.forEach(el => {
      if (el.style) {
        el.style.zIndex = '2147483647'
      }
    })
  }

  let tyroObserver = null
  const startTyroObserver = () => {
    if (tyroObserver) return
    tyroObserver = new MutationObserver(() => {
      bringTyroToFront()
    })
    tyroObserver.observe(document.body, {childList: true, subtree: true})
  }

  const stopTyroObserver = () => {
    if (tyroObserver) {
      tyroObserver.disconnect()
      tyroObserver = null
    }
  }

  let lastApiKey = null

  const initialize = async (apiKey, environment = 'simulator') => {
    if (!isScriptLoaded.value) {
      await loadScript(environment)
    }

    if (typeof window.TYRO === 'undefined') {
      throw new Error('Tyro iClient not available')
    }

    if (isInitialized.value && iclientInstance && lastApiKey === apiKey) {
      console.log('[Tyro] Already initialized with same API key, skipping re-init')
      return true
    }

    try {
      if (lastApiKey && lastApiKey !== apiKey) {
        console.log('[Tyro] API key changed, clearing cached credentials')
        await clearCachedCredentials()
      }

      lastApiKey = apiKey
      console.log('[Tyro] Creating IClientWithUI with API key:', apiKey ? '***' + apiKey.slice(-4) : 'none')
      iclientInstance = new window.TYRO.IClientWithUI(apiKey || '', getPosProductInfo())
      isInitialized.value = true
      connectionState.value = 'initialized'

      console.log('[Tyro] iClient initialized, isInitialized.value =', isInitialized.value)
      return true
    } catch (error) {
      console.error('[Tyro] Failed to initialize:', error)
      lastError.value = error.message
      connectionState.value = 'error'
      throw error
    }
  }

  const pairTerminal = async (mid, tid) => {
    if (!iclientInstance) {
      throw new Error('Tyro iClient not initialized')
    }

    return new Promise((resolve, reject) => {
      currentStatus.value = 'Pairing...'
      connectionState.value = 'pairing'

      const timeout = setTimeout(() => {
        connectionState.value = 'error'
        reject(new Error('Terminal pairing timeout'))
      }, 120000)

      iclientInstance.pairTerminal(mid, tid, (response) => {
        console.log('[Tyro] Pairing response:', redactSensitive(response))

        if (response.status === 'success') {
          clearTimeout(timeout)
          isPaired.value = true
          currentTerminalId = tid
          terminalInfo.value = response
          integrationKey.value = response.integrationKey || null
          currentStatus.value = 'Paired'
          connectionState.value = 'connected'
          console.log('[Tyro] Pairing successful, integration key:', response.integrationKey ? '***' + response.integrationKey.slice(-4) : 'none')
          savePairingState(tid, true)
          resolve(response)
        } else if (response.status === 'inProgress') {
          currentStatus.value = response.message || 'Waiting for terminal authorisation...'
          console.log('[Tyro] Pairing in progress, waiting for completion...')
        } else {
          clearTimeout(timeout)
          isPaired.value = false
          lastError.value = response.message || 'Pairing failed'
          currentStatus.value = 'Pairing failed'
          connectionState.value = 'error'
          reject(new Error(response.message || 'Pairing failed'))
        }
      })
    })
  }

  const pairTerminalWithRetry = async (mid, tid, maxRetries = RETRY_CONFIG.maxRetries) => {
    let lastErr = null

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        retryAttempt.value = attempt
        if (attempt > 0) {
          const delay = calculateBackoff(attempt - 1)
          currentStatus.value = `Reconnecting in ${Math.round(delay / 1000)}s... (attempt ${attempt + 1}/${maxRetries})`
          await sleep(delay)
        }

        const result = await pairTerminal(mid, tid)
        retryAttempt.value = 0
        return result
      } catch (error) {
        lastErr = error
        const errorCode = classifyError(error)
        console.warn(`[Tyro] Pairing attempt ${attempt + 1} failed:`, errorCode, error.message)

        if (!isRetryableError(errorCode)) {
          break
        }
      }
    }

    retryAttempt.value = 0
    throw lastErr
  }

  const initiatePurchase = async (amount, options = {}) => {
    if (!isInitialized.value) {
      throw new Error('Tyro iClient not initialized')
    }

    if (!isPaired.value) {
      const terminalId = options.terminalId || currentTerminalId
      console.log('[Tyro] isPaired is false, checking SQLite cache for terminalId:', terminalId)
      const cachedPairing = await checkCachedPairing(terminalId)
      if (cachedPairing) {
        console.log('[Tyro] Found cached pairing in SQLite, setting isPaired to true')
        isPaired.value = true
        currentTerminalId = terminalId
      } else {
        throw new Error('Tyro terminal not paired. Please pair the terminal from Devices page first.')
      }
    }

    isProcessing.value = true
    currentStatus.value = 'Initiating payment...'
    lastError.value = null

    startTyroObserver()

    const amountInCents = String(Math.round(Number(amount) * 100))
    const cashoutInCents = String(Math.round(Number(options.cashout || 0) * 100))

    if (options.orderId) {
      storePendingTransaction(options.orderId, amount)
    }

    const purchaseOptions = {
      amount: amountInCents,
      cashout: cashoutInCents,
      integratedReceipt: true,
      enableSurcharge: options.enableSurcharge !== false,
    }

    if (options.merchantId) {
      purchaseOptions.mid = options.merchantId
    }
    if (options.terminalId) {
      purchaseOptions.tid = options.terminalId
    }

    console.log('[Tyro] Purchase options:', purchaseOptions)
    console.log('[Tyro] iclientInstance:', iclientInstance)
    console.log('[Tyro] isPaired:', isPaired.value)

    return new Promise((resolve, reject) => {
      let transactionTimeout = null
      let capturedMerchantReceipt = null

      const cleanup = () => {
        stopTyroObserver()
        if (transactionTimeout) {
          clearTimeout(transactionTimeout)
          transactionTimeout = null
        }
      }

      if (options.timeout) {
        transactionTimeout = setTimeout(() => {
          isProcessing.value = false
          currentStatus.value = 'Transaction timeout'
          lastError.value = 'Transaction timed out'
          reject({
            success: false,
            result: 'TIMEOUT',
            message: 'Transaction timed out - please check terminal status',
            errorCode: ERROR_CODES.TIMEOUT,
          })
        }, options.timeout)
      }

      const callbacks = {
        questionCallback: (question, answerCallback) => {
          console.log('[Tyro] Question:', question.text, 'Options:', question.options)
          if (options.onQuestion) {
            options.onQuestion(question, answerCallback)
            return
          }
          // No question handler. Defaulting to options[0] silently consents
          // to whatever the terminal asked — DCC at a worse FX rate, signature
          // verify (transferring chargeback liability to the merchant), DCC
          // confirmations, etc. Bias toward the safer "no/quit/decline"
          // option when present; otherwise reject the transaction explicitly
          // rather than guessing.
          const opts = question.options || []
          const safeOption = opts.find((o) => {
            const u = String(o).toUpperCase()
            return u === 'NO' || u === 'QUIT' || u === 'DECLINE' || u === 'REJECT' || u === 'CANCEL'
          })
          if (safeOption) {
            console.warn('[Tyro] No question handler — defaulting to safe option:', safeOption, 'for question:', question.text)
            answerCallback(safeOption)
            return
          }
          // No safe option recognised. Pick the FIRST option as a last resort
          // but loud-warn so it's visible in field logs. The caller really
          // should set onQuestion in production.
          console.error('[Tyro] No question handler AND no recognised safe option. Question:', question.text, 'Options:', opts, '— picking first option as last resort. Set options.onQuestion to handle this properly.')
          answerCallback(opts[0])
        },

        statusMessageCallback: (message) => {
          console.log('[Tyro] Status message:', message)
          currentStatus.value = message
          if (options.onStatusUpdate) {
            options.onStatusUpdate(message)
          }
        },

        receiptCallback: (receipt) => {
          console.log('[Tyro] Receipt:', redactSensitive(receipt))
          console.log('[Tyro] Signature required:', receipt.signatureRequired)
          if (receipt.merchantReceipt) {
            capturedMerchantReceipt = receipt.merchantReceipt
          }
          if (options.onReceipt) {
            options.onReceipt({
              ...receipt,
              signatureRequired: receipt.signatureRequired || false,
            })
          }
        },

        transactionCompleteCallback: (response) => {
          cleanup()
          clearPendingTransaction()
          console.log('[Tyro] Transaction complete:', redactSensitive(response))
          isProcessing.value = false

          if (response.result === 'APPROVED') {
            currentStatus.value = 'Approved'
            resolve({
              success: true,
              transactionId: response.transactionId,
              amount: amountInCents / 100,
              cardType: response.cardType,
              last4: response.elidedPan?.slice(-4) || response.cardDetails?.pan?.slice(-4),
              customerReceipt: response.customerReceipt,
              merchantReceipt: response.merchantReceipt || capturedMerchantReceipt,
              surchargeAmount: response.surchargeAmount,
              tipAmount: response.tipAmount,
              baseAmount: response.baseAmount,
              transactionReference: response.transactionReference,
              authorisationCode: response.authorisationCode,
              rawResponse: response,
            })
          } else if (response.result === 'CANCELLED') {
            currentStatus.value = 'Cancelled'
            reject({
              success: false,
              result: response.result,
              message: 'Transaction cancelled',
              customerReceipt: response.customerReceipt,
              merchantReceipt: response.merchantReceipt || capturedMerchantReceipt,
              rawResponse: response,
              errorCode: ERROR_CODES.CANCELLED,
            })
          } else if (response.result === 'DECLINED') {
            currentStatus.value = 'Declined'
            reject({
              success: false,
              result: response.result,
              message: response.responseText || 'Card declined',
              customerReceipt: response.customerReceipt,
              merchantReceipt: response.merchantReceipt || capturedMerchantReceipt,
              rawResponse: response,
              errorCode: ERROR_CODES.DECLINED,
            })
          } else if (response.result === 'REVERSED') {
            currentStatus.value = 'Reversed'
            reject({
              success: false,
              result: response.result,
              message: 'Transaction reversed',
              customerReceipt: response.customerReceipt,
              merchantReceipt: response.merchantReceipt || capturedMerchantReceipt,
              rawResponse: response,
              errorCode: ERROR_CODES.UNKNOWN,
            })
          } else if (response.result === 'SYSTEM ERROR') {
            currentStatus.value = 'System Error'
            reject({
              success: false,
              result: response.result,
              message: response.responseText || 'Terminal system error',
              customerReceipt: response.customerReceipt,
              merchantReceipt: response.merchantReceipt || capturedMerchantReceipt,
              rawResponse: response,
              errorCode: ERROR_CODES.TERMINAL_DISCONNECTED,
            })
          } else {
            currentStatus.value = response.result || 'Failed'
            lastError.value = response.responseText || response.result
            reject({
              success: false,
              result: response.result,
              message: response.responseText || response.result,
              customerReceipt: response.customerReceipt,
              merchantReceipt: response.merchantReceipt || capturedMerchantReceipt,
              rawResponse: response,
              errorCode: classifyError({result: response.result, message: response.responseText}),
            })
          }
        },
      }

      try {
        iclientInstance.initiatePurchase(purchaseOptions, callbacks)
      } catch (error) {
        cleanup()
        isProcessing.value = false
        lastError.value = error.message
        console.error('[Tyro] initiatePurchase error:', error)
        reject({
          success: false,
          result: 'ERROR',
          message: error.message,
          errorCode: classifyError(error),
        })
      }
    })
  }

  const initiatePurchaseWithRetry = async (amount, options = {}) => {
    const maxRetries = options.maxRetries || RETRY_CONFIG.maxRetries
    const maxTotalTimeoutMs = options.maxTotalTimeout || 300000
    const startTime = Date.now()
    let lastErr = null

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      const elapsed = Date.now() - startTime
      if (elapsed > maxTotalTimeoutMs) {
        console.log('[Tyro] Maximum total timeout exceeded, stopping retries')
        lastErr = new Error('Payment timeout - maximum retry time exceeded')
        break
      }

      try {
        retryAttempt.value = attempt

        if (attempt > 0) {
          const delay = calculateBackoff(attempt - 1)
          currentStatus.value = `Retrying payment in ${Math.round(delay / 1000)}s... (attempt ${attempt + 1}/${maxRetries})`
          if (options.onStatusUpdate) {
            options.onStatusUpdate(currentStatus.value)
          }
          await sleep(delay)
        }

        const result = await initiatePurchase(amount, options)
        retryAttempt.value = 0
        return result
      } catch (error) {
        lastErr = error
        const errorCode = error.errorCode || classifyError(error)
        console.warn(`[Tyro] Payment attempt ${attempt + 1} failed:`, errorCode, error.message)

        if (!isRetryableError(errorCode)) {
          console.log('[Tyro] Non-retryable error, stopping retries')
          break
        }

        if (options.onRetry) {
          options.onRetry(attempt + 1, maxRetries, error)
        }
      }
    }

    retryAttempt.value = 0
    throw lastErr
  }

  const initiateRefund = async (amount, options = {}) => {
    if (!iclientInstance) {
      throw new Error('Tyro iClient not initialized')
    }

    isProcessing.value = true
    currentStatus.value = 'Initiating refund...'
    lastError.value = null

    const amountInCents = String(Math.round(Number(amount) * 100))

    const refundOptions = {
      amount: amountInCents,
      integratedReceipt: true,
    }

    if (options.merchantId) {
      refundOptions.mid = options.merchantId
    }
    if (options.terminalId) {
      refundOptions.tid = options.terminalId
    }

    console.log('[Tyro] Refund options:', refundOptions)

    return new Promise((resolve, reject) => {
      let capturedMerchantReceipt = null

      const callbacks = {
        statusMessageCallback: (message) => {
          console.log('[Tyro] Refund status:', message)
          currentStatus.value = message
          if (options.onStatusUpdate) {
            options.onStatusUpdate(message)
          }
        },

        receiptCallback: (receipt) => {
          console.log('[Tyro] Refund receipt:', redactSensitive(receipt))
          console.log('[Tyro] Refund signature required:', receipt.signatureRequired)
          if (receipt.merchantReceipt) {
            capturedMerchantReceipt = receipt.merchantReceipt
          }
          if (options.onReceipt) {
            options.onReceipt({
              ...receipt,
              signatureRequired: receipt.signatureRequired || false,
            })
          }
        },

        transactionCompleteCallback: (response) => {
          console.log('[Tyro] Refund complete:', redactSensitive(response))
          isProcessing.value = false

          const merchantReceipt = response.merchantReceipt || capturedMerchantReceipt

          if (response.result === 'APPROVED') {
            currentStatus.value = 'Refund approved'
            resolve({
              success: true,
              refundId: response.transactionId,
              amount: amountInCents / 100,
              cardType: response.cardType,
              last4: response.elidedPan?.slice(-4) || response.cardDetails?.pan?.slice(-4),
              customerReceipt: response.customerReceipt,
              merchantReceipt,
              receipt: response.customerReceipt || merchantReceipt,
              rawResponse: response,
            })
          } else {
            currentStatus.value = response.result || 'Refund failed'
            lastError.value = response.responseText || response.result
            reject({
              success: false,
              result: response.result,
              message: response.responseText || response.result,
              customerReceipt: response.customerReceipt,
              merchantReceipt,
              rawResponse: response,
            })
          }
        },
      }

      try {
        iclientInstance.initiateRefund(refundOptions, callbacks)
      } catch (error) {
        isProcessing.value = false
        lastError.value = error.message
        reject(error)
      }
    })
  }

  const cancelTransaction = () => {
    if (!iclientInstance) {
      console.warn('[Tyro] Cannot cancel - not initialized')
      return false
    }

    try {
      iclientInstance.cancelCurrentTransaction()
      currentStatus.value = 'Cancelling...'
      console.log('[Tyro] Cancel requested')
      return true
    } catch (error) {
      console.error('[Tyro] Cancel failed:', error)
      lastError.value = error.message
      return false
    }
  }

  const storePendingTransaction = (orderId, amount) => {
    try {
      const data = {
        orderId,
        amount,
        timestamp: Date.now(),
      }
      localStorage.setItem(PENDING_TXN_KEY, JSON.stringify(data))
      hasPendingTransaction.value = true
      console.log('[Tyro] Stored pending transaction:', data)
    } catch (e) {
      console.warn('[Tyro] Failed to store pending transaction:', e)
    }
  }

  const getPendingTransaction = () => {
    try {
      const data = localStorage.getItem(PENDING_TXN_KEY)
      if (data) {
        const parsed = JSON.parse(data)
        if (Date.now() - parsed.timestamp < 3600000) {
          return parsed
        }
        clearPendingTransaction()
      }
    } catch (e) {
      console.warn('[Tyro] Failed to get pending transaction:', e)
    }
    return null
  }

  const clearPendingTransaction = () => {
    try {
      localStorage.removeItem(PENDING_TXN_KEY)
      hasPendingTransaction.value = false
      console.log('[Tyro] Cleared pending transaction')
    } catch (e) {
      console.warn('[Tyro] Failed to clear pending transaction:', e)
    }
  }

  const checkForPendingTransaction = () => {
    const pending = getPendingTransaction()
    hasPendingTransaction.value = !!pending
    return pending
  }

  const continueLastTransaction = async (options = {}) => {
    if (!iclientInstance) {
      throw new Error('Tyro iClient not initialized')
    }

    console.log('[Tyro] Attempting to continue last transaction...')
    currentStatus.value = 'Recovering transaction...'
    isProcessing.value = true

    return new Promise((resolve, reject) => {
      let capturedMerchantReceipt = null

      const timeout = setTimeout(() => {
        isProcessing.value = false
        clearPendingTransaction()
        reject(new Error('Continue transaction timeout'))
      }, 60000)

      const callbacks = {
        receiptCallback: (receipt) => {
          console.log('[Tyro] Recovery receipt:', redactSensitive(receipt))
          if (receipt.merchantReceipt) {
            capturedMerchantReceipt = receipt.merchantReceipt
          }
          if (options.onReceipt) {
            options.onReceipt(receipt)
          }
        },

        transactionCompleteCallback: (response) => {
          clearTimeout(timeout)
          console.log('[Tyro] Recovery transaction complete:', redactSensitive(response))
          isProcessing.value = false
          clearPendingTransaction()

          if (response.result === 'APPROVED') {
            currentStatus.value = 'Transaction recovered - Approved'
            resolve({
              success: true,
              recovered: true,
              transactionId: response.transactionId,
              amount: response.baseAmount ? Number(response.baseAmount) / 100 : null,
              cardType: response.cardType,
              last4: response.elidedPan?.slice(-4) || response.cardDetails?.pan?.slice(-4),
              customerReceipt: response.customerReceipt,
              merchantReceipt: response.merchantReceipt || capturedMerchantReceipt,
              surchargeAmount: response.surchargeAmount,
              tipAmount: response.tipAmount,
              rawResponse: response,
            })
          } else if (response.result === 'NOT STARTED' || response.result === 'NONE') {
            currentStatus.value = 'No pending transaction'
            resolve({
              success: true,
              recovered: false,
              noPendingTransaction: true,
              message: 'No pending transaction to recover',
            })
          } else {
            currentStatus.value = response.result || 'Recovery failed'
            resolve({
              success: false,
              recovered: true,
              result: response.result,
              message: response.responseText || response.result,
              rawResponse: response,
            })
          }
        },
      }

      try {
        iclientInstance.continueLastTransaction(callbacks)
      } catch (error) {
        clearTimeout(timeout)
        isProcessing.value = false
        console.error('[Tyro] continueLastTransaction error:', error)
        reject(error)
      }
    })
  }

  const getTerminalInfo = async () => {
    if (!iclientInstance) {
      throw new Error('Tyro iClient not initialized')
    }

    if (typeof iclientInstance.terminalInfo === 'function') {
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Terminal info request timeout'))
        }, 15000)

        iclientInstance.terminalInfo((response) => {
          clearTimeout(timeout)
          console.log('[Tyro] Terminal info:', response)
          if (response.status === 'success') {
            terminalInfo.value = response
            connectionState.value = 'connected'
            resolve(response)
          } else {
            connectionState.value = 'error'
            reject(new Error(response.message || 'Failed to get terminal info'))
          }
        })
      })
    } else {
      console.log('[Tyro] terminalInfo not available in IClientWithUI, returning cached info')
      return {
        status: isPaired.value ? 'paired' : 'not_paired',
        message: 'Terminal info not available with IClientWithUI (use headless iClient for full terminal info)',
        isPaired: isPaired.value,
        isInitialized: isInitialized.value,
        connectionState: connectionState.value,
      }
    }
  }

  const checkConnection = async () => {
    try {
      await getTerminalInfo()
      return {connected: true, status: connectionState.value}
    } catch (error) {
      connectionState.value = 'disconnected'
      return {connected: false, status: connectionState.value, error: error.message}
    }
  }

  const scheduleReconnect = (mid, tid, delay = 5000) => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
    }

    reconnectTimer = setTimeout(async () => {
      console.log('[Tyro] Attempting automatic reconnection...')
      try {
        await pairTerminalWithRetry(mid, tid, 3)
        console.log('[Tyro] Reconnection successful')
      } catch (error) {
        console.error('[Tyro] Reconnection failed:', error)
        scheduleReconnect(mid, tid, Math.min(delay * 2, 60000))
      }
    }, delay)
  }

  const stopReconnect = () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
  }

  const checkBrowserCompatibility = () => {
    if (!iclientInstance && typeof window.TYRO !== 'undefined') {
      return window.TYRO.isBrowserCompatible ? window.TYRO.isBrowserCompatible() : true
    }
    if (iclientInstance && iclientInstance.isBrowserCompatible) {
      return iclientInstance.isBrowserCompatible()
    }
    const ua = navigator.userAgent.toLowerCase()
    return ua.includes('chrome') || ua.includes('firefox') || ua.includes('safari') || ua.includes('edge')
  }

  const reset = () => {
    isProcessing.value = false
    currentStatus.value = ''
    lastError.value = null
    retryAttempt.value = 0
    stopReconnect()
    stopTyroObserver()
  }

  const disconnect = () => {
    reset()
    isPaired.value = false
    isInitialized.value = false
    connectionState.value = 'disconnected'
    iclientInstance = null
  }

  const getReconciliationReport = async (options = {}) => {
    if (!iclientInstance) {
      throw new Error('Tyro iClient not initialized')
    }

    const today = new Date()
    const defaultDate = today.toISOString().slice(0, 10).replace(/-/g, '')

    const requestParams = {
      terminalBusinessDay: options.date || defaultDate,
      type: options.type || 'summary',
      format: options.format || 'txt',
    }

    if (options.mid) {
      requestParams.mid = options.mid
    }
    if (options.tid) {
      requestParams.tid = options.tid
    }
    if (options.integrationKey || integrationKey.value) {
      requestParams.integrationKey = options.integrationKey || integrationKey.value
    }

    console.log('[Tyro] Requesting reconciliation report:', requestParams)

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Reconciliation report request timeout'))
      }, 30000)

      iclientInstance.reconciliationReport(requestParams, (response) => {
        clearTimeout(timeout)
        console.log('[Tyro] Reconciliation report response:', redactSensitive(response))

        if (response.result === 'success' || response.data) {
          resolve({
            success: true,
            data: response.data,
            result: response.result,
            format: requestParams.format,
            type: requestParams.type,
            date: requestParams.terminalBusinessDay,
          })
        } else {
          reject({
            success: false,
            result: response.result,
            error: response.error || 'Failed to retrieve report',
            message: response.error || response.result,
          })
        }
      })
    })
  }

  const getReconciliationReportForDateRange = async (startDate, endDate, options = {}) => {
    const reports = []
    const start = new Date(startDate)
    const end = new Date(endDate)
    const maxDays = 7

    let dayCount = 0
    for (let d = new Date(start); d <= end && dayCount < maxDays; d.setDate(d.getDate() + 1)) {
      dayCount++
      const dateStr = d.toISOString().slice(0, 10).replace(/-/g, '')
      try {
        const report = await getReconciliationReport({
          ...options,
          date: dateStr,
        })
        reports.push(report)
      } catch (error) {
        console.warn(`[Tyro] Failed to get report for ${dateStr}:`, error)
        reports.push({
          success: false,
          date: dateStr,
          error: error.message || error.error,
        })
      }
    }

    return reports
  }

  return {
    isScriptLoaded,
    isInitialized,
    isPaired,
    isProcessing,
    currentStatus,
    lastError,
    terminalInfo,
    connectionState,
    retryAttempt,
    hasPendingTransaction,
    integrationKey,

    loadScript,
    initialize,
    clearCachedCredentials,
    checkCachedPairing,
    savePairingState,
    pairTerminal,
    pairTerminalWithRetry,
    initiatePurchase,
    initiatePurchaseWithRetry,
    initiateRefund,
    cancelTransaction,
    continueLastTransaction,
    storePendingTransaction,
    getPendingTransaction,
    clearPendingTransaction,
    checkForPendingTransaction,
    getTerminalInfo,
    checkConnection,
    scheduleReconnect,
    stopReconnect,
    checkBrowserCompatibility,
    reset,
    disconnect,
    getReconciliationReport,
    getReconciliationReportForDateRange,

    classifyError,
    isRetryableError,
    ERROR_CODES,
    TYRO_URLS,
    getPosProductInfo,
  }
}
